import { memo, useEffect, useMemo, useState } from 'react';
import { Box, Link, Stack, Typography } from '@mui/material';
import { directApiEndpoints, processorEndpoints, whitelistEndpoints } from './content';

export type NavigationGroup = {
    readonly title: string;
    readonly highlighted?: boolean;
    readonly links: readonly (readonly [id: string, label: string])[];
};

export const navigation: readonly NavigationGroup[] = [
    {
        title: 'Getting started',
        links: [
            ['overview', 'Overview'],
            ['environments', 'Environments'],
            ['authentication', 'Authentication'],
            ['transaction-flow', 'Transaction flow']
        ]
    },
    {
        title: 'Payment Processor',
        links: processorEndpoints.map(endpoint => [endpoint.id, endpoint.title] as const)
    },
    {
        title: 'Integration',
        links: [
            ['go-live-testing', 'Before going live'],
            ['notifications', 'Notifications'],
            ['webhook', 'Webhook redirect'],
            ['live-payment-methods', 'Payment methods'],
            ['statuses', 'Statuses'],
            ['errors', 'Error messages']
        ]
    },
    {
        title: 'Whitelist imports',
        highlighted: true,
        links: [
            ['whitelist-process', 'Whitelist process'],
            ['whitelist-environments', 'Environments'],
            ['hashed-email-whitelist', 'Hashed email whitelist'],
            ...whitelistEndpoints.map(endpoint => [endpoint.id, endpoint.title] as const)
        ]
    },
    {
        title: 'Reporting and Payout API',
        links: [
            ['direct-list-parameters', 'List request parameters'],
            ...directApiEndpoints.map(endpoint => [endpoint.id, endpoint.title] as const)
        ]
    }
];

export const groupSectionIds = (title: string) =>
    navigation.find(group => group.title === title)?.links.map(([id]) => id) ?? [];

const allSectionIds = navigation.flatMap(group => group.links.map(([id]) => id));

/** Tracks which section is currently at the top of the viewport. */
const useActiveSection = () => {
    const [activeId, setActiveId] = useState<string>();

    useEffect(() => {
        const elements = allSectionIds
            .map(id => document.getElementById(id))
            .filter((element): element is HTMLElement => !!element);
        // Sections already reserve room for the sticky headers via scroll-margin-top; reuse that offset.
        const headerOffset = elements.length ? parseFloat(window.getComputedStyle(elements[0]).scrollMarginTop) || 96 : 96;
        const visible = new Set<string>();
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    visible.add(entry.target.id);
                } else {
                    visible.delete(entry.target.id);
                }
            });
            const firstVisible = allSectionIds.find(id => visible.has(id));
            if (firstVisible) {
                setActiveId(firstVisible);
            }
        }, { rootMargin: `-${Math.round(headerOffset)}px 0px -55% 0px` });

        elements.forEach(element => observer.observe(element));

        return () => observer.disconnect();
    }, []);

    return activeId;
};

const DocumentationNav = ({ matches, onNavigate }: {
    readonly matches: ReadonlySet<string> | null;
    readonly onNavigate?: () => void;
}) => {
    const activeId = useActiveSection();
    const groups = useMemo(() => navigation
        .map(group => ({
            ...group,
            links: matches ? group.links.filter(([id]) => matches.has(id)) : group.links
        }))
        .filter(group => group.links.length), [matches]);

    if (!groups.length) {
        return (
            <Typography variant='body2' color='text.secondary' sx={{ px: 0.5 }}>
                No matching sections.
            </Typography>
        );
    }

    return (
        <>
            {groups.map(group => (
                <Box
                    key={group.title}
                    sx={group.highlighted ? {
                        mb: 3,
                        p: 1.5,
                        border: 1,
                        borderColor: 'primary.main',
                        borderRadius: 1.5,
                        bgcolor: 'primary.lighter'
                    } : { mb: 3 }}
                >
                    <Link
                        href={`#${group.links[0][0]}`}
                        onClick={onNavigate}
                        underline='none'
                        variant='overline'
                        color={group.highlighted ? 'primary.main' : 'text.secondary'}
                        sx={{
                            display: 'block',
                            px: 0.5,
                            fontWeight: 700,
                            borderRadius: 1,
                            '&:hover': { color: 'text.primary' }
                        }}
                    >
                        {group.title}
                    </Link>
                    <Stack component='ul' sx={{ mt: 0.5, p: 0, listStyle: 'none' }}>
                        {group.links.map(([id, label]) => {
                            const isActive = id === activeId;

                            return (
                                <li key={id}>
                                    <Link
                                        href={`#${id}`}
                                        onClick={onNavigate}
                                        underline='none'
                                        aria-current={isActive ? 'location' : undefined}
                                        color={isActive ? 'primary.main' : 'text.secondary'}
                                        sx={{
                                            display: 'block',
                                            px: 1,
                                            py: 0.55,
                                            borderLeft: 2,
                                            borderColor: isActive ? 'primary.main' : 'transparent',
                                            borderRadius: '0 4px 4px 0',
                                            fontSize: 14,
                                            fontWeight: isActive ? 600 : 400,
                                            '&:hover': {
                                                bgcolor: 'action.hover',
                                                color: 'text.primary'
                                            }
                                        }}
                                    >
                                        {label}
                                    </Link>
                                </li>
                            );
                        })}
                    </Stack>
                </Box>
            ))}
        </>
    );
};

export default memo(DocumentationNav);
